import { FormComponent } from './form-component';
import {
  Observable,
  Subscription
} from 'rxjs';
import {
  FormGroupState,
  SetValueAction,
  RemoveArrayControlAction
} from 'ngrx-forms';
import {
  Inject,
  OnDestroy,
  OnInit,
  Input,
  Optional
} from '@angular/core';
import {
  FORM_CONTAINER_CONTEXT,
  FormContainerContext
} from '../tokens';
import {
  Store,
  select
} from '@ngrx/store';
import {
  filter,
  map,
  first,
  tap
} from 'rxjs/operators';
import { FormState } from './forms.reducer';
import {
  getForm,
  getFormState
} from './forms.selectors';
import { DigiAIXCoreAppState } from '../app-state';
import {
  getValueByObjectPath,
  buildNewFormArray
} from './helpers';
import { NGXLogger } from 'ngx-logger';
import { IWithIdentifier } from '@digitaix/types';
import { DxSetValueAction } from './forms.actions';

/**
 * @deprecated use AbstractFormComponent2
 */
export class AbstractFormComponent<
  Form,
  AppState extends DigiAIXCoreAppState = DigiAIXCoreAppState,
  R extends IWithIdentifier = Form & IWithIdentifier
> implements FormComponent<Form>, OnDestroy, OnInit {
  @Input() public formState$!: Observable<FormGroupState<Form>>;
  @Input() public formId: string;
  public collectionPath: string;
  /**
   * @deprecated
   */
  public readonly context: FormContainerContext<R>;

  protected _subscriptions = new Subscription();

  constructor(
    @Optional() @Inject(FORM_CONTAINER_CONTEXT) context: any,
    public readonly store$: Store<AppState>,
    public logger: NGXLogger
  ) {
    // tslint:disable-next-line: deprecation
    this.context = context;
    if (context) {
      this.formId = context.formId;
      this.formState$ = context.formGroupState$.pipe(
        filter(formState => !!formState)
      );
    } else {
      this.logger.warn(
        '[AbstractFormComponent::constructor] FORM_CONTAINER_CONTEXT not set'
      );
    }
  }

  public ngOnInit(): void {
    if (!this.formId) {
      throw new Error('Form id is not set');
    }
    if (!this.formState$) {
      this.formState$ = this.store$
        .select(getFormState<Form, FormGroupState<Form>>(this.formId))
        .pipe(
          tap(formState =>
            this.logger.debug(
              '[AbstractFormComponent::ngOnInit] formState$',
              formState
            )
          )
        );
    }
    // TODO : add super call to child classes
    this._subscriptions.add(
      (this.store$ as any)
        .pipe(
          select(getForm<Form, any>(this.formId)),
          map(
            (formState: FormState<Form, any>) =>
              formState.form.userDefinedProperties.collectionPath ||
              formState.collectionPath
          ),
          tap(collectionPath =>
            this.logger.debug(
              '[AbstractFormComponent::ngOnInit] collectionPath',
              collectionPath
            )
          )
        )
        .subscribe(collectionPath => (this.collectionPath = collectionPath))
    );
  }

  public add(path: string, start = true): void {
    this._subscriptions.add(
      (this.store$ as any)
        .pipe(
          select(getForm<Form, any>(this.formId)),
          first(),
          tap(formState =>
            this.logger.debug(
              '[AbstractFormComponent::add] formState',
              formState
            )
          ),
          map((formState: FormState<Form, any>) => {
            const formArrayValue: ReadonlyArray<{
              order: number;
            }> | null = getValueByObjectPath(formState.form.value, path);

            if (!formArrayValue || !Array.isArray(formArrayValue)) {
              this.logger.error(
                '[AbstractFormComponent::add] formArrayValue is not set or is not an array',
                {
                  formState,
                  formId: this.formId,
                  formArrayValue,
                  path
                }
              );
              throw new Error('Form array value not found');
            }

            return new DxSetValueAction(
              [this.formId, path].join('.'),
              buildNewFormArray(
                formArrayValue,
                getValueByObjectPath(formState.arrayInitial, path),
                start
              ),
              `AbstractFormDefinition(${this.constructor.name})::add::${path}`
            );
          }),
          tap(action =>
            this.logger.debug(
              '[AbstractFormComponent::add] SetValueAction',
              action
            )
          )
        )
        .subscribe(this.store$)
    );
  }

  public remove(id: string): void {
    this.store$.dispatch(
      new RemoveArrayControlAction(
        id.replace(/\.[0-9]+$/, ''),
        Number(id.match(/\.([0-9]+)$/)[1])
      )
    );
  }

  public trackBy(index, item) {
    return item.id;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
